import { ReactElement, useEffect, useRef, useState } from "react";
import Toolbar from "./components/toolbar";
import {
  ComponentType,
  IAcquisitionChartConfig,
  IDashboard, IDoughnutConfig,
  IEventConfig, IGaugeIndicatorConfig,
  IGripPosition,
  IIndicatorConfig, IRadarConfig
} from "./types";
import { getGripLocation } from "./utils";
import Component from "./components/component";
import { useAsyncFetch, useFetch } from "../../system/useFetch";
import { logger } from "../../system/utils";
import { IRealtimeData, ISelectedNode, IState } from "../../types";
import ProductionGantt from "./components/widgets/gantt/productionGantt";
import BrowserItems from "./components/browserItems";
import { useSelector } from "react-redux";
import { DashboardSchemaType } from "../interruptions/types";
import useDimensions from "../../system/useDimensions";

interface IProps {
  defaultDashboard?: IDashboard;
  parentSize: {
    width: number;
    height: number;
  };
}

const defaultScan = 5000;

const DashboardPage = ({ parentSize, defaultDashboard }: IProps): ReactElement => {
  const [dashboard, setDashboard] = useState<IDashboard | null>(defaultDashboard ?? null);
  const [panelPosition, setPanelPosition] = useState<IGripPosition | null>(null);
  const [dashboardID, setDashboardID] = useState<number>(0);
  const [runtime, setRuntime] = useState<boolean>(false);
  const [selectedComponent, seSelectedComponent] = useState<string | null>(null);
  const [realtime, setRealtime] = useState<Record<string, IRealtimeData>>({});
  const [getRealtimeData] = useAsyncFetch<Record<string, IRealtimeData>>();
  const intervalRef = useRef<any>(null);
  const selectedNode = useSelector<IState, ISelectedNode>(state => state.selectedNode);
  const [ref, dimensions] = useDimensions<HTMLDivElement>();
  const { data: schemas } = useFetch<Record<DashboardSchemaType, any>>(
    {
      module: "dashboard",
      query: "getSchemaConfig",
      payload: {
        nodeID: selectedNode.nodeID,
        nodeType: selectedNode.type
      }
    });

  useEffect(() => {
    if (dashboard && dashboard.customConfig.autoStart && dashboardID !== dashboard.dashboardID) {
      setDashboardID(dashboard.dashboardID!);
      setRuntime(true);
    }
  }, [ dashboard ]);

  useEffect(() => {
    if(runtime && dashboard) {
      const variables: Set<string> = new Set();

      dashboard.layout.components.forEach(c => {
        if (c.componentType === ComponentType.Indicators) {
          (c.config as IIndicatorConfig).variables.forEach((v) => {
            variables.add(v.code);
            (v.conditions ?? []).forEach(cx => {
              if (cx.type === "TAG" && cx.value !== "") {
                variables.add(cx.value as string);
              }
            });
          });
        } else if (c.componentType === ComponentType.Events) {
          (c.config as IEventConfig).variables.forEach((v) => {
            variables.add(v.code);
            v.conditions.forEach(condition => {
              if(condition.type === "TAG" && condition.value !== ""){
                variables.add(condition.value as string);
              }
            });
          });
        } else if (c.componentType === ComponentType.Doughnut) {
          (c.config as IDoughnutConfig).variables.forEach((v) => variables.add(v.code));
        } else if (c.componentType === ComponentType.Gauge) {
          (c.config as IGaugeIndicatorConfig).variables.forEach((v) => {
            variables.add(v.code);
            v.segments.forEach(s => {
              if (s.start.type === "TAG" && s.start.value !== "") {
                variables.add(s.start.value as string);
              }

              if (s.end.type === "TAG" && s.end.value !== "") {
                variables.add(s.end.value as string);
              }
            });

            (v.conditions ?? []).forEach(cx => {
              if (cx.type === "TAG" && cx.value !== "") {
                variables.add(cx.value as string);
              }
            });
          });
        } else if (c.componentType === ComponentType.Radar) {
          (c.config as IRadarConfig).variables.forEach((v) => {
            variables.add(v.code);
            if (v.limits.aim.type === "TAG" && v.limits.aim.value !== "") {
              variables.add(v.limits.aim.value as string);
            } else if (v.limits.aim.type === "TAG" && v.limits.minimum.value !== "") {
              variables.add(v.limits.minimum.value as string);
            } else if (v.limits.aim.type === "TAG" && v.limits.maximum.value !== "") {
              variables.add(v.limits.maximum.value as string);
            }
          });
        } else if (c.componentType === ComponentType.AcquisitionChart) {
          const acquisitionCell = (c.config as IAcquisitionChartConfig).acquisitionCell;

          acquisitionCell.variables.forEach((v) => {
            variables.add(v.field);
            v.limits.forEach(l => {
              if(l.variable) {
                variables.add(l.variable);
              }
            });
          });
        }
      });

      const tags = [ ...variables ].filter(t => t && t !== "" && t !== null && t !== undefined);

      const callback = () => {
        getRealtimeData({
          module: "acquisition",
          query: "getRealtimeValues",
          payload: tags
        }, true).then(result => {
          if (!result){
            return;
          }

          const newRecord = { ...realtime };

          Object.keys(result).map(k => {
            if (result[k]) {
              const isNumeric = !result[k].Type || result[k].Type === "N";

              if (newRecord[k]) {
                newRecord[k].AcquisitionTimeStamp = result[k].AcquisitionTimeStamp;
                newRecord[k].Error = result[k].Error;
                newRecord[k].Value = isNumeric ? parseFloat(`${result[k].Value}`) : result[k].Value;
              } else {
                newRecord[k] = {
                  AcquisitionTimeStamp: result[k].AcquisitionTimeStamp,
                  Error: result[k].Error,
                  Value: isNumeric ? parseFloat(`${result[k].Value}`) : result[k].Value
                };
              }
            } else {
              newRecord[k] = {
                AcquisitionTimeStamp: 0,
                Error: 1,
                Value: 0
              };
            }
          });

          setRealtime(newRecord);
        });
      };

      if (tags.length > 0) {
        callback();
        intervalRef.current = setInterval(callback, dashboard.customConfig.scan ?? defaultScan);
      }
    }

    return () => {
      if (intervalRef.current) {
        logger("DashboardPage.ClearInterval...", intervalRef.current);
        clearInterval(intervalRef.current as any);
        intervalRef.current = null;
      }
    };
  }, [ runtime, dashboard ]);

  const parentStyle = {
    gridTemplateColumns: "1fr",
    gridTemplateRows: "auto auto 1fr",
    gridRowGap: ".5em",
    justifyItems: "stretch",
    alignItems: "stretch",
    justifyContent: "stretch",
    alignContent: "stretch",
    ...parentSize,
    maxHeight: parentSize.height
  };

  const h = dimensions.height - 4;
  const rows = dashboard ? dashboard.customConfig.rows ?? 12 : 0;
  const columns = dashboard ? dashboard.customConfig.columns ?? 12 : 0;
  const rowGap = (rows - 1) * 7;
  const columnGap = (columns - 1) * 7;

  const layoutStyle = {
    gridTemplateColumns: `repeat(${columns}, ${(dimensions.width - columnGap) / columns}px)`,
    gridTemplateRows: `repeat(${rows}, ${(h - rowGap) / rows}px)`,
    gridGap: ".5em",
    justifyItems: "stretch",
    alignItems: "stretch",
    overflow: "hidden",
    fontSize: `${dashboard ? dashboard.customConfig.fontSize ?? 1 : 1}em`,
    height: h,
    maxHeight: h,
    width: dimensions.width
  };

  return (
    <div className="display-grid" style={parentStyle}>
      {!defaultDashboard && dashboard &&
        <Toolbar selectedNode={selectedNode} dashboard={dashboard}
          setDashboard={setDashboard} runtime={runtime} setRuntime={setRuntime}
          setPanelPosition={setPanelPosition}/>}

      {dashboard && <ProductionGantt dashboard={dashboard}/>}

      {!dashboard && <BrowserItems setDashboard={setDashboard} selectedNode={selectedNode} />  }

      <div style={{ gridRow: "3 / span 1" }} ref={ref}>
        {dashboard && schemas &&
          <div className="display-grid" style={layoutStyle}>
            {dashboard.layout.components.map((component) => {

              return (
                <Component key={`d${dashboard.dashboardID ?? 0}-component-${component.key}`}
                  component={component} loading={false} dashboard={dashboard} setDashboard={setDashboard}
                  selectedComponent={selectedComponent} seSelectedComponent={seSelectedComponent}
                  disableSettings={false} runtime={runtime} realtime={realtime}
                  setPanelPosition={setPanelPosition} schemas={schemas}/>
              );
            })}

            {panelPosition && <div style={{
              ...getGripLocation(panelPosition),
              borderRadius: "4px",
              background: "rgba(100, 150, 255, 0.2)",
              zIndex: 999
            }}/>}
          </div>
        }
        </div>
    </div>
  );
};

export default DashboardPage;
